import { W, H, project, PLANET_COLORS, LINE_KIND } from "./constants";

const label = (a) => `${a.planet} · ${a.city_display || a.city || ""}`;
const anchorFor = (a, fallbackLng) => {
  const [x, y] = project(typeof a.lat === "number" ? a.lat : 0, typeof a.lng === "number" ? a.lng : fallbackLng);
  return { x, y };
};

// Split a projected polyline at longitude wraps so we never draw a slash across the map.
function curvePath(points) {
  const projected = points.map((p) => project(p.lat, p.lng, W, H));
  const segments = [];
  let cur = [];
  for (const [x, y] of projected) {
    if (cur.length && Math.abs(x - cur[cur.length - 1][0]) > W * 0.6) {
      segments.push(cur);
      cur = [];
    }
    cur.push([x, y]);
  }
  if (cur.length) segments.push(cur);
  return segments.map((seg) => "M " + seg.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(" L ")).join(" ");
}

function lineGeometry(a, i, cx, cy) {
  if (a.type === "vertical" && typeof a.longitude === "number") {
    const [x] = project(0, a.longitude);
    return { d: `M ${x} 0 L ${x} ${H}`, anchor: anchorFor(a, a.longitude), isVertical: true };
  }
  if (a.type === "curve" && Array.isArray(a.points) && a.points.length) {
    return { d: curvePath(a.points), anchor: anchorFor(a, 0), isVertical: false };
  }
  // Legacy fallback: small bezier from active city → planet's anchor city
  const [ax, ay] = project(a.lat || 0, a.lng || 0, W, H);
  const mx = (cx + ax) / 2 + (i % 2 === 0 ? -30 : 30);
  const my = (cy + ay) / 2 - 40;
  return { d: `M ${cx} ${cy} Q ${mx} ${my} ${ax} ${ay}`, anchor: { x: ax, y: ay }, isVertical: false };
}

// Greedy AABB anti-collision: stack labels down, then up, until a free slot appears.
function stackLabels(lines) {
  const boxes = [];
  const fs = 9;
  return lines.map((l) => {
    const labelW = l.label.length * fs * 0.6 + 12;
    const labelH = fs + 4;
    const baseX = l.anchor.x + 8;
    const baseY = l.anchor.y - 6;
    let picked = { x: baseX, y: baseY };
    for (const dy of [0, 14, -14, 28, -28, 42, -42, 56, -56]) {
      const cy2 = baseY + dy;
      const box = [baseX - 2, cy2 - labelH + 2, baseX - 2 + labelW, cy2 + 4];
      const overlaps = boxes.some((o) => !(box[2] < o[0] || box[0] > o[2] || box[3] < o[1] || box[1] > o[3]));
      if (!overlaps) {
        picked = { x: baseX, y: cy2 };
        boxes.push(box);
        break;
      }
    }
    return { ...l, labelAnchor: picked };
  });
}

// Full-earth astro line geometry (vertical MC/IC + AC/DC horizon curves) with collision-adjusted labels.
export function layoutAstroLines(astroLines, visibleLines, cx, cy) {
  const drawn = astroLines
    .filter((a) => visibleLines[a.planet] !== false)
    .map((a, i) => {
      const isAngle = (a.kind === "angle") || LINE_KIND[a.planet] === "angle";
      return {
        key: `line-${a.planet}`,
        color: PLANET_COLORS[a.planet] || "#C8A96E",
        dashArray: isAngle ? "2 6" : "6 4",
        strokeWidth: isAngle ? 1.4 : 1.2,
        strokeOpacity: isAngle ? 0.85 : 0.75,
        delay: i * 0.12,
        label: label(a),
        ...lineGeometry(a, i, cx, cy),
      };
    });
  return stackLabels(drawn);
}

// Dedupe previous pins by city and cluster by pixel proximity so stacked labels stay legible.
export function clusterPrevPins(previousPins, radius = 26) {
  const seenCity = new Set();
  const clusters = [];
  for (const p of previousPins) {
    const key = (p.city || "").toLowerCase();
    if (!key || seenCity.has(key)) continue;
    seenCity.add(key);
    const [x, y] = project(p.lat, p.lng, W, H);
    const pt = { x, y, city: p.city, easter_egg: !!p.easter_egg };
    const target = clusters.find((c) => Math.hypot(c.x - pt.x, c.y - pt.y) <= radius);
    if (target) {
      target.items.push(pt);
      target.x = (target.x * (target.items.length - 1) + pt.x) / target.items.length;
      target.y = (target.y * (target.items.length - 1) + pt.y) / target.items.length;
      if (pt.easter_egg) target.isEgg = true;
    } else {
      clusters.push({ x: pt.x, y: pt.y, items: [pt], isEgg: pt.easter_egg });
    }
  }
  return clusters;
}
